import { Instrument } from "./music-objects";

export class Genre {
	public readonly genreId: string;
	public readonly genreName: string;

	public constructor(genreId: string, genreName: string) {
		this.genreId = genreId;
		this.genreName = genreName;
	}

	public static fromJson(json: any): Genre {
		return new Genre(
			json.genreId,
			json.genreName
		);
	}
}

export class Artist {
	public readonly artistId: string;
	public readonly artistName: string;
	public readonly artistType: string;

	public constructor(artistId: string, artistName: string, artistType: string) {
		this.artistId = artistId;
		this.artistName = artistName;
		this.artistType = artistType;
	}

	public static fromJson(json: any): Artist {
		return new Artist(
			json.artistId,
			json.artistName,
			json.artistType
		);
	}
}

export class Song {
	public readonly songId: string;
	public readonly songName: string;
	public readonly processed: boolean;
	public readonly uploadDate: Date;
	public readonly availableToGuests: boolean;
	public readonly instruments: Instrument[];
	public readonly genres: Genre[];
	public readonly artists: Artist[];
	public readonly drags: SongDrag[];
	public readonly previews: SongPreviews[];
	public readonly usage: SongUsage[];
	public readonly submissionUsage: SongSubmissionUsage[];
	public readonly price?: number;
	public readonly tags?: string[];

	public constructor(
		songId: string,
		songName: string,
		processed: boolean,
		uploadDate: Date,
		availableToGuests: boolean,
		instruments: Instrument[],
		genres: Genre[],
		artists: Artist[],
		drags: SongDrag[],
		previews: SongPreviews[],
		usage: SongUsage[],
		submissionUsage: SongSubmissionUsage[],
		price?: number
	) {
		this.songId = songId;
		this.songName = songName;
		this.processed = processed;
		this.uploadDate = uploadDate;
		this.availableToGuests = availableToGuests;
		this.instruments = instruments;
		this.genres = genres;
		this.artists = artists;
		this.drags = drags;
		this.previews = previews;
		this.usage = usage;
		this.submissionUsage = submissionUsage;
		this.price = price;
		// Collect tags from instruments, genres, and artists
		this.tags = artists.map(artist => artist.artistName)
			.concat(genres.map(genre => genre.genreName))
			.concat(instruments.map(instrument => instrument.name));
	}

	public setAvailableToGuests(newValue: boolean): Song {
		return new Song(
			this.songId,
			this.songName,
			this.processed,
			this.uploadDate,
			newValue,
			this.instruments,
			this.genres,
			this.artists,
			this.drags,
			this.previews,
			this.usage,
			this.submissionUsage,
			this.price
		);
	}

	public static fromJson(json: any): Song {
		return new Song(
			json.songId,
			json.songName,
			json.processed,
			new Date(json.uploadDate),
			json.availableToGuests,
			json.instruments.map((instrument: any) => Instrument.fromJson(instrument)),
			json.genres.map((genre: any) => Genre.fromJson(genre)),
			json.artists.map((artist: any) => Artist.fromJson(artist)),
			json.drags ?? [],
			json.previews ?? [],
			json.usage ?? [],
			json.submissionUsage ?? [],
			json.price
		);
	}
}

export class SongPage {
	public readonly pageIndex: number;
	public readonly totalPages: number;
	public readonly totalSongs: number;
	public readonly genreFilter?: Genre;
	public readonly songs: Song[];

	public constructor(pageIndex: number, totalPages: number, totalSongs: number, songs: Song[], genreFilter?: Genre) {
		this.pageIndex = pageIndex;
		this.totalPages = totalPages;
		this.totalSongs = totalSongs;
		this.songs = songs;
		this.genreFilter = genreFilter;
	}

	public static fromJson(json: any): SongPage {
		return new SongPage(
			json.pageIndex,
			json.totalPages,
			json.totalSongs,
			json.songs.map((song: any) => Song.fromJson(song)),
			json.genreFilter ? Genre.fromJson(json.genreFilter) : undefined
		);
	}
}

export class SongDrag {
	public readonly dragId!: string;
	public readonly timestamp!: Date;
	public readonly measureCount!: number;
	public readonly partName!: string;
	public readonly sectionName!: string;
}

export class SongPreviews {
	public readonly previewId!: string;
	public readonly timestamp!: Date;
	public readonly partName!: string;
	public readonly sectionName!: string;
}

export class SongUsage {
	public readonly songUsageId!: string;
	public readonly songId!: string;
	public readonly projectId!: string;
	public readonly submissionCount!: number;
	public readonly measuresUsed!: number;
	public readonly partName!: string;
	public readonly sectionName!: string;
}

export class SongSubmissionUsage {
	public readonly songSubmissionUsageId!: string;
	public readonly timestamp!: Date;
	public readonly songId!: string;
	public readonly projectSubmissionId!: string;
	public readonly measuresUsed!: number;
	public readonly partName!: string;
	public readonly sectionName!: string;
}